<template>
  <v-card id="search-view-card" class="mx-auto search-view-cards">
    <v-card-text>
      <analyze-search v-model="searchedItem" @keydown.enter="search" @click:clear="clearSearch" />
      <v-spacer />
      <div id="listContainer" class="list-container" style="height: 100%">
        <div :style="tableStyle">
          <analyze-data-table
            v-if="isTableHightSet && computedTableOptions.headers && computedTableOptions.items"
            table-id="search-view-table-id"
            :headers="computedTableOptions.headers"
            :items="computedTableOptions.items"
            data-type="artifacts"
            :page="serverOptions.page"
            :total-items="serverItemsLength"
            :load-items="loadArtifactsFromServer"
            :loading="loading"
            :has-server-sided-pagination="true"
            @select="showRow"
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
  import { Artifact, Snapshot, Project } from '@/models'
  import { ArtifactItem } from '@/services/ISearchService'
  import { computed, ref, watch } from 'vue'
  import { parseIntUndefined } from '@/utils/helpers'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { useProjectStore } from '@/stores/projectStore'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import labels from '@/translations/labels.json'
  import ColorConverterService from '@/services/impl/ColorConverterService.impl'
  import { ArtifactAttribute } from '@/models/artifact-attribute.model'
  import { useTraceDataStore } from '@/stores/traceDataStore'
  import { useRoute, useRouter } from 'vue-router'
  import { useRouteStore } from '@/stores/routeStore'
  import { EventBus } from '@/event-bus'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { injectTyped } from '@/inject'
  import { ServiceKeys } from '@/services/symbols'

  const props = defineProps({
    height: { type: Number, required: true },
  })
  const snapshotStore = useSnapshotStore()
  const projectStore = useProjectStore()
  const traceDataStore = useTraceDataStore()
  const routeStore = useRouteStore()
  const router = useRouter()
  const route = useRoute()
  const searchedItem = ref<string>('')

  const currentSnapshot = computed((): Snapshot | undefined => {
    return snapshotStore.currentSnapshot
  })

  const currentProject = computed((): Project | undefined => {
    return projectStore.currentProject
  })

  const searchService = injectTyped(ServiceKeys.search)
  const colorConverterService: ColorConverterService = new ColorConverterService()

  const serverOptions = ref<{ page: number; rowsPerPage: number }>({
    page: 1,
    rowsPerPage: 10,
  })

  const serverItemsLength = ref<number>(0)
  const items = ref<ArtifactItem[]>([])
  const loading = ref(false)

  const searchViewHeaders = ref<AnalyzeTableHeader[]>([
    { title: '', key: 'iconprovider', sortable: false },
    { title: 'Artifact', key: 'artifactitem', sortable: false },
  ])

  const searchViewItems = ref<any[]>([])

  const computedTableOptions = computed(() => {
    let headers: AnalyzeTableHeader[] = []
    let items: AnalyzeTableItem<ArtifactItem>[] = []

    headers = searchViewHeaders.value
    items = searchViewItems.value
    return { headers, items }
  })

  const isTableHightSet = computed((): boolean => {
    return searchViewItems.value.length > 0 || loading.value
  })

  const tableStyle = computed((): string => {
    return isTableHightSet.value
      ? `height: ${props.height}px; overflow-y: scroll;`
      : 'overflow-y: scroll;'
  })

  const clearSearch = () => {
    EventBus.emit('searchedArtifacts', [])
    serverOptions.value = {
      page: 1,
      rowsPerPage: 25,
    }
    items.value = []
    searchViewItems.value = []
    loading.value = false
    searchedItem.value = ''
    traceDataStore.searchMode = false
  }

  const search = async () => {
    traceDataStore.searchMode = true
    serverOptions.value = {
      page: 1,
      rowsPerPage: 25,
    }
    // Reset server options or define initial load parameters
    const options = {
      page: 1,
      itemsPerPage: serverOptions.value.rowsPerPage, // Use your default or current setting
      // Add sortBy and sortDesc if needed, for example:
      // sortBy: ['someField'],
      // sortDesc: [true]
    }
    loadArtifactsFromServer(options)
  }

  const loadArtifactsFromServer = async (options: {
    page: number
    itemsPerPage: number
    sortBy?: string[]
    sortDesc?: boolean[]
  }) => {
    const { page, itemsPerPage } = options

    loading.value = true

    searchViewItems.value = []
    serverOptions.value.page = page
    serverOptions.value.rowsPerPage = itemsPerPage
    if (currentProject.value && currentSnapshot.value) {
      const snapshot = currentSnapshot.value
      searchService
        .searchSnapshotArtifacts(
          searchedItem.value,
          currentProject.value.id,
          snapshot.id,
          serverOptions.value.page,
          parseIntUndefined(route.query.filter as string),
          serverOptions.value.rowsPerPage,
        )
        .then((data: any) => {
          serverItemsLength.value = data.pagination.totalElements
          items.value = buildTreeviewItems(data.data)
          searchViewItems.value = items.value.map((item: ArtifactItem) => ({
            artifactitem: item.name,
            iconprovider: item,
            value: item,
          }))
          loading.value = false
          EventBus.emit('searchedArtifacts', data.data)
        })
        .catch(() => {
          /* ts-ignore */
        })
    }
  }

  watch(currentSnapshot, () => {
    clearSearch()
  })

  function getArtifactTypeColor(color: string, name: string): string {
    return colorConverterService.convertArtifactTypeColorToCssColor(color, name)
  }

  function buildTreeviewItems(artifacts: Array<Artifact>): ArtifactItem[] {
    return artifacts.map((artifact: Artifact) => {
      const artifactItem = {
        id: artifact.id,
        name: artifact.name,
        artifactType: artifact.artifactType.name,
        artifactTypeColor: getArtifactTypeColor(
          artifact.artifactType.color,
          artifact.artifactType.name,
        ),
        provider: artifact.artifactType.provider,
      } as ArtifactItem

      if (artifact.attributes !== undefined) {
        artifactItem.children = artifact.attributes.map((attr: ArtifactAttribute) => {
          return { id: attr.id, name: attr.name, value: attr.value }
        })
      }
      return artifactItem
    })
  }

  function selectArtifact(artifactId: string): void {
    traceDataStore.setArtifactId(artifactId)
    router.push({
      name: labels.SIDEBAR.MAIN.EXPLORER_GRAPH,
      params: {
        projectId: currentProject.value?.id.toString(),
        snapshotId: currentSnapshot.value?.id.toString(),
        artifactId: artifactId,
      },
      query: {
        filter: route.query.filter,
      },
    })
    routeStore.setArtifactId(artifactId)
  }

  const showRow = (item: ArtifactItem) => {
    const currentArtifact = item
    selectArtifact(currentArtifact.id.toString())
  }
</script>

<style src="./SearchView.scss" lang="scss" scoped>
  .list-container {
    overflow: hidden;
    height: 90%;
  }

  .artifact-item {
    white-space: normal;
    font-size: 14px;
  }

  .artifact-type-name {
    color: gray;
  }

  .artifact-item {
    white-space: normal;
    font-size: 14px;
  }

  .artifact-type-name {
    color: gray;
  }
</style>
